import { createAsyncThunk, createSlice, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import { DriverMileageClient, DriverMileageDto } from '@acorn-portal/shared/src/services/driverMileageApi';

interface DriverMileageState {
	mileage: DriverMileageDto | null;
	loading: boolean;
	error: string | null;
}

const initialState: DriverMileageState = {
	mileage: null,
	loading: false,
	error: null,
};

interface FetchDriverMileageParams {
	policyToken: string;
}

export const fetchDriverMileage = createAsyncThunk<
	DriverMileageDto,
	FetchDriverMileageParams,
	{ rejectValue: string }
>('driverMileage/fetchDriverMileage', async (params: FetchDriverMileageParams, { rejectWithValue }) => {
	try {
		const client = new DriverMileageClient(undefined, API);
		const response = await client.getDriverMileage(params.policyToken);

		if (response === null || response === undefined) {
			return rejectWithValue('No mileage data available for this policy');
		}

		return response;
	} catch (e) {
		if (axios.isAxiosError(e) && e.response) {
			if (e.response.status < 200 || e.response.status > 300) {
				return rejectWithValue(e.message ?? 'Failed to fetch driver mileage');
			}
		}
		if (typeThrownException(e) && e.status) {
			if (e.status < 200 || e.status > 300) {
				return rejectWithValue(e.message ?? 'Failed to fetch driver mileage');
			}
		}
		return rejectWithValue('Failed to fetch driver mileage');
	}
});

export const driverMileageSlice = createSlice({
	name: 'driverMileage',
	initialState,
	reducers: {
		clearDriverMileage: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<DriverMileageState>) => {
		builder.addCase(fetchDriverMileage.pending, (state: DriverMileageState) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(fetchDriverMileage.fulfilled, (state: DriverMileageState, { payload }) => {
			state.loading = false;
			state.mileage = payload;
		});
		builder.addCase(fetchDriverMileage.rejected, (state: DriverMileageState, action) => {
			state.loading = false;
			state.error = action.payload ?? action.error.message ?? 'Failed to fetch driver mileage';
		});
	},
});

export const { clearDriverMileage } = driverMileageSlice.actions;
export default driverMileageSlice.reducer;
